<template>
	<div>
		<v-container align="center" justify="center" class="pa-5 pb-0">
			<v-list-item three-line>
				<v-list-item-content>
					<v-list-item-title class="mb-1 text-center text-wrap"><h1>Experience</h1></v-list-item-title>
					<v-list-item-subtitle class="text-center text-wrap">Make your profile standout, tell us where you have worked.</v-list-item-subtitle>
				</v-list-item-content>
			</v-list-item>
		</v-container>

		<v-container>
			<v-form ref="locumExperienceForm" v-model="valid" class="pa-4 pt-0">
				<v-card-text align="center" justify="center" class="justify-center text-center">
					<v-menu
						ref="experienceStartMenu"
						v-model="experienceStartMenu"
						offset-y
						:close-on-content-click="false"
						transition="scale-transition"
						max-width="320px"
						min-width="290px"
					>
						<template #activator="{ on }">
							<v-text-field
								v-model="dateFormatted"
								outlined
								class="pb-3"
								label="Select experience start date"
								append-icon="event"
								hint="Select or type the date (DD/MM/YYYY)"
								persistent-hint
								:rules="experiencestartRules"
								@blur="setDate"
								v-on="on"
							></v-text-field>
						</template>
						<v-date-picker v-model="experienceStartDate" :max="new Date().toISOString()" no-title @input="experienceStartMenu = false"></v-date-picker>
					</v-menu>
				</v-card-text>

				<v-card-text v-for="industry in workedAtOptions" :key="industry.form" align="center" justify="center" class="justify-center text-center pb-2 text-wrap">
					<v-row class="pb-2" align="center" justify="center"
						><strong>{{ workedAtLabel(industry.label) }} you've worked at</strong></v-row
					>
					<v-row dense>
						<v-col v-for="option in industry.options" :key="option.form" cols="12" sm="6" md="4">
							<v-container>
								<v-checkbox v-model="companiesWorkedFor" multiple color="primary" :label="option" :value="option" class="pa-0 ma-0"></v-checkbox>
							</v-container>
						</v-col>
					</v-row>
				</v-card-text>

				<v-card-text v-if="industryItems">
					<div align="center" justify="center">
						<strong class="pb-2">Skills and Training</strong>
					</div>
					<div class="mt-4">
						<v-menu :close-on-content-click="false" max-height="350" min-width="250" top>
							<template #activator="{ on, attrs }">
								<v-chip outlined class="ma-1" v-bind="attrs" v-on="on">
									<v-icon left> mdi-plus </v-icon>
									Add Skills
								</v-chip>
							</template>
							<v-sheet>
								<v-treeview v-model="industryTree" selectable item-disabled="locked" :items="industryItems">
									<template v-if="!props.item.children || props.item.nodeActive" slot="prepend" slot-scope="props">
										<v-checkbox
											v-model="props.item.selected"
											:disabled="props.item.locked || props.item.nodeActive"
											:indeterminate="props.item.nodeActive"
											@change="treeChanged($event, props.item)"
										/>
									</template>
								</v-treeview>
							</v-sheet>
						</v-menu>
						<v-chip v-for="(skill, i) of selectedSkills" :key="i" class="ma-1" close close-icon="mdi-close" @click:close="removeSkill(skill.code)">{{
							skill.name
						}}</v-chip>
					</div>
				</v-card-text>

				<v-card-text>
					<v-row
						><v-col>
							<v-select
								v-model="currentEmploymentStatus"
								:items="employmentStatusOptions"
								label="Current employment status"
								outlined
								:rules="employmentStatusRules"
								item-text="label"
								item-value="key"
							>
							</v-select> </v-col
					></v-row>
				</v-card-text>
			</v-form>
		</v-container>
	</div>
</template>

<script>
import { bus } from '@/main';
import axios from 'axios';
import store from '@/store';
import moment from 'moment';
import { Users } from '@/models/user.model';
function formatDate(date) {
	if (!date) return null;

	const [year, month, day] = date.split('-');
	return `${day}/${month}/${year}`;
}

export default {
	components: {},

	data: () => ({
		valid: false,
		step: 'experience-step',
		industriesV2: store.getters.configs.industriesV2,
		industryTree: [],
		experienceItems: [],
		experienceStartMenu: false,
		experienceStartDate: null,
		dateFormatted: formatDate(new Date().toISOString().substr(0, 10)),
		experiencestart: null,
		experiencestartRules: [
			v => !!v || 'Required',
			v => (v && moment(v, 'DD/MM/YYYY', true).isValid()) || 'Date is not valid, the date format should be DD/MM/YYYY',
			v => (v && moment(v, 'DD/MM/YYYY', true).isSameOrBefore(moment())) || 'Date can not be in the future',
		],
		companiesWorkedFor: [],
		employmentStatusOptions: Users.EMPLOYMENT_STATUS_OPTIONS,
		employmentStatusRules: [v => !!v || 'Required'],
	}),

	computed: {
		user() {
			return store.getters.user;
		},
		industryObjectV2() {
			const { industry, profession } = this.user || {};
			if (industry && profession && this.industriesV2) {
				return this.industriesV2[industry]?.professions.find(({ name }) => name === profession);
			}
			return {};
		},
		industryItems() {
			if (this.industryObjectV2 && this.industryObjectV2.skills) {
				const { skills = [], allowMultiple: allowMultipleSkills = false } = this.industryObjectV2;

				return skills.map(({ name: skillName, code, options, allowMultiple: allowMultipleOptions = true }) => {
					const mappedOptions =
						options?.map(option => {
							const siblingOptions = options?.filter(filterOption => option.name !== filterOption.name).map(mapOption => mapOption.code) || [];
							return {
								name: option.name,
								id: option.code,
								selected: this.industryTree.includes(option.code),
								allowMultiple: allowMultipleOptions,
								siblings: siblingOptions,
							};
						}) || undefined;

					const optionSelected = mappedOptions?.some(option => option.selected) || false;
					const siblingSkills = skills?.filter(filterSkill => filterSkill.name !== skillName).map(mapSkill => mapSkill.code) || [];
					const siblingOptions =
						mappedOptions?.reduce((optionCodes, { siblings = [] }) => {
							optionCodes = [...optionCodes, ...siblings];
							return optionCodes;
						}, []) || [];
					const allSiblings = [...siblingSkills, ...siblingOptions];

					return {
						children: mappedOptions || undefined,
						id: code,
						name: skillName,
						selected: this.industryTree.includes(code),
						nodeActive: optionSelected,
						allowMultiple: allowMultipleSkills,
						siblings: allSiblings,
					};
				});
			} else {
				return undefined;
			}
		},
		availableSkills() {
			if (this.industryObjectV2?.skills) {
				const skills = this.industryObjectV2.skills;
				let flattenedSkills = [];
				for (let skill of skills) {
					if (skill.options?.length) {
						for (let option of skill.options) {
							flattenedSkills.push({
								code: option.code,
								name: `${skill.name} - ${option.name}`,
							});
						}
					} else {
						flattenedSkills.push(skill);
					}
				}

				return flattenedSkills;
			} else {
				return [];
			}
		},
		selectedSkills() {
			if (!this.industryTree.length) return [];

			return this.industryTree.map(x => {
				const skill = this.availableSkills.find(y => y.code === x);
				return skill;
			});
		},
		workedAtOptions() {
			const { configs, user } = store.getters;
			return configs.industriesV2[user.industry].workedAtOptions;
		},
	},

	watch: {
		experienceStartDate() {
			this.dateFormatted = this.formatDate(this.experienceStartDate);
		},
	},

	mounted() {
		/*
			[INFO]: We parse the date from the server from MM/DD/YYYY to DD/MM/YYYY and parse it back when sending it to the server
			This will need be refactored when the server is updated with the correct format
		*/
		this.dateFormatted = 'experience' in store.getters.user ? this.formatDateFromServer(store.getters.user.experience.start_date) : null;
		this.experienceStartDate = 'experience' in store.getters.user ? this.parseDateFromServerFormat(store.getters.user.experience.start_date) : null;
		this.companiesWorkedFor = store.getters.user.experience.companies_worked_for;
		this.currentEmploymentStatus = store.getters.user.currentEmploymentStatus;

		window.scrollTo(0, 0);

		bus.$on('intent:submit-forms', data => {
			let valid = this.validate();
			if (valid) {
				bus.$emit('update:spinning', true);
				this.postToBackend(data);
			} else {
				window.scrollTo(0, 0);
			}
		});

		if (this.user.skills) {
			this.industryTree = this.user.skills;
		}
	},

	beforeDestroy() {
		bus.$off('intent:submit-forms');
	},

	methods: {
		treeChanged(isChecked, { id, allowMultiple, siblings = [] }) {
			if (isChecked) {
				if (!allowMultiple) {
					if (!this.industryObjectV2?.allowMultiple) {
						this.industryTree = [id];
					}
					this.industryTree.push(id);
					const selectedSiblings = siblings.filter(sibling => this.industryTree.includes(sibling)) || [];
					if (selectedSiblings?.length) {
						this.industryTree = this.industryTree.filter(item => !selectedSiblings.includes(item));
					}
				} else {
					this.industryTree.push(id);
				}
			} else {
				this.industryTree = this.industryTree.filter(item => item !== id);
			}
		},

		removeSkill(code) {
			const index = this.industryTree.indexOf(code);
			if (index > -1) {
				this.industryTree.splice(index, 1);
			}
		},

		setDate() {
			this.experienceStartDate = this.parseDate(this.dateFormatted);
		},

		validate() {
			return this.$refs.locumExperienceForm.validate();
		},

		reset() {
			this.$refs.locumExperienceForm.reset();
		},

		resetValidation() {
			this.$refs.locumExperienceForm.resetValidation();
		},

		formatDate,

		formatDateFromServer(date) {
			if (!date) return null;

			const [month, day, year] = date.split('/');
			return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
		},

		parseDate(date) {
			if (!date) return null;

			const [day, month, year] = date.split('/');
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
		},

		parseDateFromServerFormat(date) {
			if (!date) return null;

			const [month, day, year] = date.split('/');
			return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
		},

		parseDateToServerFormat(date) {
			if (!date) return null;

			const [day, month, year] = date.split('/');
			return `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
		},

		SubmitForm() {
			bus.$emit('intent:submit-forms');
		},

		checkStep(user) {
			let { start_date, companies_worked_for } = user.experience;

			if (start_date && companies_worked_for && companies_worked_for.length >= 1) {
				store.commit('SET_PROFILE_BUTTON_TO_POSITIVE', this.step);
				return true;
			} else if (start_date) {
				return true;
			} else {
				return false;
			}
		},

		workedAtLabel(name) {
			if (name === 'General') {
				return 'Practices';
			}
			if (name === 'Vet') {
				return 'Places';
			}
			return name;
		},

		async postToBackend() {
			const token = store.getters.jwtToken;

			const body = {
				start_date: this.parseDateToServerFormat(this.dateFormatted),
				companies_worked_for: this.companiesWorkedFor,
				currentEmploymentStatus: this.currentEmploymentStatus,
			};

			if (this.industryTree) {
				body.skills = this.industryTree;
			}

			if (body.start_date && body.companies_worked_for && body.companies_worked_for.length >= 1) {
				store.commit('SET_PROFILE_BUTTON_TO_POSITIVE', this.step);
			} else {
				store.commit('SET_PROFILE_BUTTON_TO_NEGATIVE', this.step);
			}

			const authHeader = { headers: { Authorization: token } };

			try {
				const response = await axios.put(store.getters.getAPI + '/professionals/experience', body, { headers: { Authorization: token } });

				bus.$emit('form-submitted', { form: this.name, status: response.status });

				if (response.status === 200) {
					await axios.put(`${store.getters.getAPI}/calendar/professional/availability/update-all/${store.getters.user.id}`, { experience: body }, authHeader);
					store.commit('SET_USER', response.data.data); //refresh the users state. await it completed.
					bus.$emit('intent:next-step', 'experience-step');
				}
			} finally {
				bus.$emit('update:spinning', false);
			}
		},
	},
};
</script>
